import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  TSectionEntityViewResizeHandlePointerDown,
  SectionEntityViewResizeHandle,
} from "./SectionEntityViewResizeHandle";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
  websiteBuilderResizeContainer,
  websiteBuilderResizeElement,
  websiteBuilderUpdateSelectedNode,
} from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { clamp } from "lodash";
import { SectionEntityViewIndicator, SECTION_ENTITY_VIEW_INDICATOR_HEIGHT, SECTION_ENTITY_VIEW_INDICATOR_OFFSET, TSectionEntityViewIndicator } from "../helpers/SectionEntityViewIndicator";
import { useWebsiteBuilderEditorContext } from "pages/secure/facility/websiteBuilder/editor/websiteBuilderEditorContext";
import { TWebsiteBuilderSectionEntityResizeHandle, TWebsiteBuilderSectionEntityResizeHandleCategory, WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_HEIGHT, WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_WIDTH } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderSectionEntity";
import { IWebsiteBuilderNodeDimensions, IWebsiteBuilderNodePosition } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderNodeGeometry";
import { isWebsiteBuilderContainerViewContext, isWebsiteBuilderElementViewContext, isWebsiteBuilderSectionEntityInSectionContext, TWebsiteBuilderSectionEntityViewContext } from "../helpers/sectionEntityViewContext";
import { TWebsiteBuilderElementType } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderElement";
import { websiteBuilderGetSectionScreenPosition } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import { TWebsiteBuilderContainerType } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderContainer";
import { isWebsiteBuilderElementFlowParentNode } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderNode";

export type TUseSectionEntityViewResizeArgs = {
  context: TWebsiteBuilderSectionEntityViewContext;
  selected: boolean;
  resizing: boolean;
  filled: boolean;
};

export type TSectionEntityViewResizeAspectRatioElementType = Extract<TWebsiteBuilderElementType, "embed" | "video">;

export const resizeElementTypes: TSectionEntityViewResizeAspectRatioElementType[] = ["embed", "video"];

export function isSectionEntityViewResizeAspectRatioElementType(
  elementType: TWebsiteBuilderElementType,
): elementType is TSectionEntityViewResizeAspectRatioElementType {
  return !!resizeElementTypes.find(type => type === elementType);
}

interface ISectionEntityViewResizeDimensionRatios {
  width: number;
  height: number;
}

interface ISectionEntityViewResizeRefState {
  pointerId: number;
  currentTarget: Element;
  prevDimensionRatios: ISectionEntityViewResizeDimensionRatios;
  widthBasedAspectRatio: boolean;
  preserveAspectRatio: boolean;
  prevPosition: IWebsiteBuilderNodePosition;
  nextPosition: IWebsiteBuilderNodePosition;
  prevDimensions: IWebsiteBuilderNodeDimensions;
  nextDimensions: IWebsiteBuilderNodeDimensions;
  resizingAllowed: boolean;
  resizing: boolean;
}

interface ISectionEntityViewResizeState {
  handle: TWebsiteBuilderSectionEntityResizeHandle;
  resizeDimensions: IWebsiteBuilderNodeDimensions;
  resizePosition: IWebsiteBuilderNodePosition;
  westBoundary: number,
  eastBoundary: number,
  northBoundary: number,
  southBoundary: number,
}

interface ISectionEntityViewHandleResizeResult {
  position: IWebsiteBuilderNodePosition;
  dimensions: IWebsiteBuilderNodeDimensions;
}

interface IUseSectionEntityViewResize {
  viewResizePosition: IWebsiteBuilderNodePosition;
  viewResizeDimensions: IWebsiteBuilderNodeDimensions;
  viewResizeHandles: JSX.Element;
  viewResizeIndicator: JSX.Element;
}

const allHandlesCategory: TWebsiteBuilderSectionEntityResizeHandleCategory = [
  "NorthWest",
  "North",
  "NorthEast",
  "West",
  "East",
  "SouthWest",
  "South",
  "SouthEast",
];

const lateralHandlesCategory: TWebsiteBuilderSectionEntityResizeHandleCategory = ["West", "East"];

const southOrLateralHandlesCategory: TWebsiteBuilderSectionEntityResizeHandleCategory = ["South", "West", "East"];

const cornerHandlesCategory: TWebsiteBuilderSectionEntityResizeHandleCategory = ["NorthWest", "NorthEast", "SouthWest", "SouthEast"];

export function useSectionEntityViewResize(args: TUseSectionEntityViewResizeArgs): IUseSectionEntityViewResize {
  const dispatch = useAppDispatch();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);

  const { activePageDomNodeRefMap, overlayLinesRef, designSpaceBoundaries } = useWebsiteBuilderEditorContext();

  const contextStretch = isWebsiteBuilderSectionEntityInSectionContext(args.context)
    ? args.context.stretch
    : null;

  const resizeRefState = useRef<ISectionEntityViewResizeRefState>({
    pointerId: null,
    currentTarget: null,
    prevDimensionRatios: null,
    widthBasedAspectRatio: null,
    preserveAspectRatio: null,
    prevPosition: null,
    nextPosition: null,
    prevDimensions: null,
    nextDimensions: null,
    resizingAllowed: false,
    resizing: false,
  });

  const [resizeState, setResizeState] = useState<ISectionEntityViewResizeState>({
    handle: null,
    resizeDimensions: null,
    resizePosition: null,
    westBoundary: null,
    eastBoundary: null,
    northBoundary: null,
    southBoundary: null,
  });

  const indicator: TSectionEntityViewIndicator =
    args.resizing ?
    {
      type: "dimensions",
      dimensions: resizeState.resizeDimensions,
      showOnTop:
        isWebsiteBuilderSectionEntityInSectionContext(args.context) &&
        (resizeState.resizePosition?.y ?? 0) > SECTION_ENTITY_VIEW_INDICATOR_HEIGHT + SECTION_ENTITY_VIEW_INDICATOR_OFFSET,
    } :
    null;

  const viewResizeDimensions: IWebsiteBuilderNodeDimensions =
    args.resizing && resizeState.resizeDimensions ?
      resizeState.resizeDimensions :
      (isWebsiteBuilderSectionEntityInSectionContext(args.context) ?
        args.context.activeScreenLayoutData.dimensions :
        args.context.activeLayoutData.dimensions
      );

  const viewResizePosition: IWebsiteBuilderNodePosition =
    args.resizing && resizeState.resizePosition ?
      resizeState.resizePosition :
      (isWebsiteBuilderSectionEntityInSectionContext(args.context) ?
        args.context.activeScreenLayoutData.position :
        args.context.activeLayoutData.position
      );

  const clearEditingMode = useCallback(() => {
    dispatch(
      websiteBuilderUpdateSelectedNode({
        selectedNodeUpdates: {
          type: args.context.node.type === "element" ? "element" : "container",
          editingMode: {
            type: "none",
          },
        },
      }),
    );
  }, [args.context.node.type]);

  const resetStates = useCallback(() => {
    resizeRefState.current = {
      pointerId: null,
      currentTarget: null,
      prevDimensionRatios: null,
      widthBasedAspectRatio: null,
      preserveAspectRatio: null,
      prevPosition: null,
      nextPosition: null,
      prevDimensions: null,
      nextDimensions: null,
      resizingAllowed: false,
      resizing: false,
    };

    setResizeState({
      handle: null,
      resizePosition: null,
      resizeDimensions: null,
      westBoundary: null,
      eastBoundary: null,
      northBoundary: null,
      southBoundary: null,
    });
  }, []);

  const handleKeydown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      const { pointerId, currentTarget } = resizeRefState.current;

      resizeRefState.current.resizingAllowed = false;

      if (pointerId != null) {
        currentTarget?.releasePointerCapture(pointerId);
      }

      clearEditingMode();
      resetStates();
    }
  }, [clearEditingMode, resetStates]);

  useEffect(() => {
    if (args.resizing) {
      document.addEventListener("keydown", handleKeydown);
    }

    return () => document.removeEventListener("keydown", handleKeydown);
  }, [args.resizing, handleKeydown]);

  const handleResize = useCallback((
    e: PointerEvent,
    handle: TWebsiteBuilderSectionEntityResizeHandle,
    rect: DOMRect,
    westBoundary: number,
    eastBoundary: number,
    northBoundary: number,
    southBoundary: number,
    dimensionRatios: ISectionEntityViewResizeDimensionRatios,
    widthBasedAspectRatio: boolean,
    preserveAspectRatio: boolean,
  ): ISectionEntityViewHandleResizeResult => {
    const originalPosition: IWebsiteBuilderNodePosition = {
      x: rect.left - westBoundary,
      y: rect.top - northBoundary,
    };

    const originalDimensions: IWebsiteBuilderNodeDimensions = {
      width: rect.width,
      height: rect.height,
    };

    const position = { ...originalPosition };

    const dimensions = { ...originalDimensions };

    const anyDiagonalDirection = handle === "NorthWest" || handle === "NorthEast" || handle === "SouthWest" || handle === "SouthEast";

    const anyWestDirection = handle === "NorthWest" || handle === "West" || handle === "SouthWest";
    const anyEastDirection = handle === "NorthEast" || handle === "East" || handle === "SouthEast";
    const anyNorthDirection = handle === "NorthWest" || handle === "North" || handle === "NorthEast";
    const anySouthDirection = handle === "SouthWest" || handle === "South" || handle === "SouthEast";

    if (anyWestDirection) {
      let x = e.clientX - westBoundary;
      let width = rect.right - e.clientX;

      if (overlayLinesRef.current.vertical != null) {
        const { anchor, x1 } = overlayLinesRef.current.vertical;

        switch (anchor) {
          case "left": {
            x = x1 - westBoundary;
            width = rect.right - x1;
            break;
          }
          case "center": {
            const halfWidth = rect.right - x1;

            x = x1 - halfWidth - westBoundary;
            width = halfWidth * 2;
            break;
          }
        }
      }

      const right = rect.right - westBoundary;
      const rightBoundary = right - WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_WIDTH;

      if (x < 0) {
        position.x = 0;
        dimensions.width = right;
      } else if (x > rightBoundary) {
        position.x = rightBoundary;
        dimensions.width = WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_WIDTH;
      } else {
        position.x = x;
        dimensions.width = width;
      }
    }

    if (anyEastDirection) {
      let width = e.clientX - rect.left;

      if (overlayLinesRef.current.vertical != null) {
        const { anchor, x1 } = overlayLinesRef.current.vertical;

        switch (anchor) {
          case "center": {
            const halfWidth = x1 - rect.left;
            width = halfWidth * 2;
            break;
          }
          case "right": {
            width = x1 - rect.left;
            break;
          }
        }
      }

      const maxWidth = eastBoundary - rect.left;

      dimensions.width = clamp(width, WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_WIDTH, maxWidth);
    }

    if (anyNorthDirection) {
      let y = e.clientY - northBoundary;
      let height = rect.bottom - e.clientY;

      if (overlayLinesRef.current.horizontal != null) {
        const { anchor, y1 } = overlayLinesRef.current.horizontal;

        switch (anchor) {
          case "top": {
            y = y1 - northBoundary;
            height = rect.bottom - y1;
            break;
          }
          case "center": {
            const halfHeight = rect.bottom - y1;

            y = y1 - halfHeight - northBoundary;
            height = halfHeight * 2;
            break;
          }
        }
      }

      const bottom = rect.bottom - northBoundary;
      const bottomBoundary = bottom - WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_HEIGHT;

      if (y < 0) {
        position.y = 0;
        dimensions.height = bottom;
      } else if (y > bottomBoundary) {
        position.y = bottomBoundary;
        dimensions.height = WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_HEIGHT;
      } else {
        position.y = y;
        dimensions.height = height;
      }
    }

    if (anySouthDirection) {
      let height = e.clientY - rect.top;

      if (overlayLinesRef.current.horizontal != null) {
        const { anchor, y1 } = overlayLinesRef.current.horizontal;

        switch (anchor) {
          case "center": {
            const halfHeight = y1 - rect.top;
            height = halfHeight * 2;
            break;
          }
          case "bottom": {
            height = y1 - rect.top;
            break;
          }
        }
      }

      const maxHeight = southBoundary - rect.top;

      dimensions.height = clamp(height, WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_HEIGHT, maxHeight);
    }

    if (anyDiagonalDirection && preserveAspectRatio) {
      const nextPosition: IWebsiteBuilderNodePosition = { ...position };
      const nextDimensions: IWebsiteBuilderNodeDimensions = { ...dimensions };

      const verticalOverlayActive = overlayLinesRef.current.vertical != null;
      const horizontalOverlayActive = overlayLinesRef.current.horizontal != null;

      const useWidthForAspectRatio =
        verticalOverlayActive && !horizontalOverlayActive ?
          true :
          horizontalOverlayActive && !verticalOverlayActive ?
            false :
            widthBasedAspectRatio;

      if (useWidthForAspectRatio) {
        const height = nextDimensions.width * dimensionRatios.height;

        if (anyNorthDirection) {
          const bottom = rect.bottom - northBoundary;
          nextPosition.y = bottom - height;
        }

        nextDimensions.height = height;
      } else {
        const width = nextDimensions.height * dimensionRatios.width;

        if (anyWestDirection) {
          const right = rect.right - westBoundary;
          nextPosition.x = right - width;
        }

        nextDimensions.width = width;
      }

      const right = rect.right - westBoundary;
      const maxWidth = anyWestDirection ? right : eastBoundary - rect.left;

      const bottom = rect.bottom - northBoundary;
      const maxHeight = anyNorthDirection ? bottom : southBoundary - rect.top;

      if (
        nextDimensions.width >= WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_WIDTH &&
        nextDimensions.width <= maxWidth &&
        nextDimensions.height >= WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_HEIGHT &&
        nextDimensions.height <= maxHeight &&
        Math.abs((nextDimensions.width / nextDimensions.height) - dimensionRatios.width) < 0.001
      ) {
        position.x = nextPosition.x;
        position.y = nextPosition.y;
        dimensions.width = nextDimensions.width;
        dimensions.height = nextDimensions.height;
      } else {
        return {
          position: originalPosition,
          dimensions: originalDimensions,
        };
      }
    }

    return {
      position,
      dimensions,
    };
  }, []);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const { resizingAllowed, resizing, prevDimensionRatios, widthBasedAspectRatio, preserveAspectRatio } = resizeRefState.current;

      if (!resizingAllowed) {
        return;
      }

      if (isWebsiteBuilderSectionEntityInSectionContext(args.context)) {
        const ref = activePageDomNodeRefMap.current.get(args.context.node.id);
        const sectionRef = activePageDomNodeRefMap.current.get(args.context.parentNode.id);

        const boundingRect = ref?.getBoundingClientRect();
        const sectionBoundingRect = sectionRef?.getBoundingClientRect();

        if (boundingRect == null || sectionBoundingRect == null) {
          return;
        }

        const { position: nextPosition, dimensions: nextDimensions } = handleResize(
          e,
          resizeState.handle,
          boundingRect,
          resizeState.westBoundary,
          resizeState.eastBoundary,
          resizeState.northBoundary,
          resizeState.southBoundary,
          prevDimensionRatios,
          widthBasedAspectRatio,
          preserveAspectRatio,
        );

        const nextScreenPosition = websiteBuilderGetSectionScreenPosition(designSpaceBoundaries.leftX, nextPosition);
        const nextScreenDimensions: IWebsiteBuilderNodeDimensions = { ...nextDimensions };

        if (contextStretch?.enabled) {
          nextScreenPosition.x = contextStretch.margin;
          nextScreenDimensions.width = designSpaceBoundaries.leftX + designSpaceBoundaries.rightX - contextStretch.margin * 2;

          nextPosition.x = 0;
          nextDimensions.width = designSpaceBoundaries.width;
        }

        resizeRefState.current.nextPosition = nextPosition;
        resizeRefState.current.nextDimensions = nextDimensions;

        setResizeState(prev => ({
          ...prev,
          resizePosition: nextScreenPosition,
          resizeDimensions: nextScreenDimensions,
        }));

        if (!resizing) {
          resizeRefState.current.resizing = true;

          if (args.context.node.type === "element") {
            dispatch(
              websiteBuilderUpdateSelectedNode({
                selectedNodeUpdates: {
                  type: "element",
                  editingMode: {
                    type: "element-resize",
                    handle: resizeState.handle,
                    context: {
                      type: "element-in-section",
                      sectionTop: sectionBoundingRect.top,
                      sectionBottom: sectionBoundingRect.bottom,
                      designSpaceLeftXBoundary: designSpaceBoundaries.leftX,
                      designSpaceRightXBoundary: designSpaceBoundaries.rightX,
                    },
                  },
                },
              }),
            );
          } else if (args.context.node.type === "container") {
            dispatch(
              websiteBuilderUpdateSelectedNode({
                selectedNodeUpdates: {
                  type: "container",
                  editingMode: {
                    type: "container-resize",
                    handle: resizeState.handle,
                    context: {
                      type: "container-in-section",
                      sectionTop: sectionBoundingRect.top,
                      sectionBottom: sectionBoundingRect.bottom,
                      designSpaceLeftXBoundary: designSpaceBoundaries.leftX,
                      designSpaceRightXBoundary: designSpaceBoundaries.rightX,
                    },
                  },
                },
              }),
            );
          }
        }
      } else if (args.context.type === "element-in-container") {
        const ref = activePageDomNodeRefMap.current.get(args.context.node.id);
        const containerRef = activePageDomNodeRefMap.current.get(args.context.parentNode.id);

        const boundingRect = ref?.getBoundingClientRect();
        const containerBoundingRect = containerRef?.getBoundingClientRect();

        if (boundingRect == null || containerBoundingRect == null) {
          return;
        }

        const { position: nextPosition, dimensions: nextDimensions } = handleResize(
          e,
          resizeState.handle,
          boundingRect,
          resizeState.westBoundary,
          resizeState.eastBoundary,
          resizeState.northBoundary,
          resizeState.southBoundary,
          prevDimensionRatios,
          widthBasedAspectRatio,
          preserveAspectRatio,
        );

        resizeRefState.current.nextPosition = nextPosition;
        resizeRefState.current.nextDimensions = nextDimensions;

        setResizeState(prev => ({
          ...prev,
          resizePosition: nextPosition,
          resizeDimensions: nextDimensions,
        }));

        if (!resizing) {
          resizeRefState.current.resizing = true;

          dispatch(
            websiteBuilderUpdateSelectedNode({
              selectedNodeUpdates: {
                type: "element",
                editingMode: {
                  type: "element-resize",
                  handle: resizeState.handle,
                  context: {
                    type: "element-in-container",
                    containerTop: containerBoundingRect.top,
                    containerRight: containerBoundingRect.right,
                    containerBottom: containerBoundingRect.bottom,
                    containerLeft: containerBoundingRect.left,
                  },
                },
              },
            }),
          );
        }
      } else if (args.context.type === "element-in-grid-cell") {
        const ref = activePageDomNodeRefMap.current.get(args.context.node.id);
        const gridRef = activePageDomNodeRefMap.current.get(args.context.gridNode.id);

        const boundingRect = ref?.getBoundingClientRect();
        const gridBoundingRect = gridRef?.getBoundingClientRect();

        if (boundingRect == null || gridBoundingRect == null) {
          return;
        }

        const { position: nextPosition, dimensions: nextDimensions } = handleResize(
          e,
          resizeState.handle,
          boundingRect,
          resizeState.westBoundary,
          resizeState.eastBoundary,
          resizeState.northBoundary,
          resizeState.southBoundary,
          prevDimensionRatios,
          widthBasedAspectRatio,
          preserveAspectRatio,
        );

        resizeRefState.current.nextPosition = nextPosition;
        resizeRefState.current.nextDimensions = nextDimensions;

        setResizeState(prev => ({
          ...prev,
          resizePosition: nextPosition,
          resizeDimensions: nextDimensions,
        }));

        if (!resizing) {
          resizeRefState.current.resizing = true;

          dispatch(
            websiteBuilderUpdateSelectedNode({
              selectedNodeUpdates: {
                type: "element",
                editingMode: {
                  type: "element-resize",
                  handle: resizeState.handle,
                  context: {
                    type: "element-in-grid-cell",
                    gridTop: gridBoundingRect.top,
                    gridRight: gridBoundingRect.right,
                    gridBottom: gridBoundingRect.bottom,
                    gridLeft: gridBoundingRect.left,
                  },
                },
              },
            }),
          );
        }
      } else if (args.context.type === "element-in-tab") {
        const ref = activePageDomNodeRefMap.current.get(args.context.node.id);
        const tabRef = activePageDomNodeRefMap.current.get(args.context.parentNode.id);

        const boundingRect = ref?.getBoundingClientRect();
        const tabBoundingRect = tabRef?.getBoundingClientRect();

        if (boundingRect == null || tabBoundingRect == null) {
          return;
        }

        const { position: nextPosition, dimensions: nextDimensions } = handleResize(
          e,
          resizeState.handle,
          boundingRect,
          resizeState.westBoundary,
          resizeState.eastBoundary,
          resizeState.northBoundary,
          resizeState.southBoundary,
          prevDimensionRatios,
          widthBasedAspectRatio,
          preserveAspectRatio,
        );

        resizeRefState.current.nextPosition = nextPosition;
        resizeRefState.current.nextDimensions = nextDimensions;

        setResizeState(prev => ({
          ...prev,
          resizePosition: nextPosition,
          resizeDimensions: nextDimensions,
        }));

        if (!resizing) {
          resizeRefState.current.resizing = true;

          dispatch(
            websiteBuilderUpdateSelectedNode({
              selectedNodeUpdates: {
                type: "element",
                editingMode: {
                  type: "element-resize",
                  handle: resizeState.handle,
                  context: {
                    type: "element-in-tab",
                    tabTop: tabBoundingRect.top,
                    tabRight: tabBoundingRect.right,
                    tabBottom: tabBoundingRect.bottom,
                    tabLeft: tabBoundingRect.left,
                  },
                },
              },
            }),
          );
        }
      }
    },
    [
      args.context,
      contextStretch,
      resizeState.handle,
      resizeState.westBoundary,
      resizeState.eastBoundary,
      resizeState.northBoundary,
      resizeState.southBoundary,
      designSpaceBoundaries.leftX,
      designSpaceBoundaries.rightX,
      designSpaceBoundaries.width,
      handleResize,
    ],
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      const { resizingAllowed, resizing, prevPosition, nextPosition, prevDimensions, nextDimensions, currentTarget } =
        resizeRefState.current;

      if (!resizingAllowed) {
        return;
      }

      if (resizing) {
        if (
          prevPosition &&
          nextPosition &&
          prevDimensions &&
          nextDimensions &&
          (Math.abs(prevDimensions.width - nextDimensions.width) > 1 ||
            Math.abs(prevDimensions.height - nextDimensions.height) > 1)
        ) {
          if (isWebsiteBuilderElementViewContext(args.context)) {
            const elementIsFlowPositioned = isWebsiteBuilderElementFlowParentNode(args.context.parentNode);

            dispatch(
              websiteBuilderResizeElement(activePageId, deviceView, args.context.node.id, {
                id: args.context.node.id,
                device: deviceView,
                prevPosition: { ...prevPosition },
                nextPosition: elementIsFlowPositioned ? { ...prevPosition } : { ...nextPosition },
                prevDimensions: { ...prevDimensions },
                nextDimensions: { ...nextDimensions },
              }),
            );
          } else if (isWebsiteBuilderContainerViewContext(args.context)) {
            dispatch(
              websiteBuilderResizeContainer(activePageId, deviceView, args.context.node.id, {
                id: args.context.node.id,
                device: deviceView,
                prevPosition: { ...prevPosition },
                nextPosition: { ...nextPosition },
                prevDimensions: { ...prevDimensions },
                nextDimensions: { ...nextDimensions },
              }),
            );
          } else {
            clearEditingMode();
          }
        } else {
          clearEditingMode();
        }
      }

      currentTarget?.releasePointerCapture(e.pointerId);

      resetStates();
    },
    [
      args.context,
      activePageId,
      deviceView,
      clearEditingMode,
      resetStates,
    ],
  );

  useEffect(() => {
    if (resizeState.handle) {
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    }

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [resizeState.handle, handlePointerMove, handlePointerUp]);

  const onPointerDown: TSectionEntityViewResizeHandlePointerDown = (e, handle) => {
    e.stopPropagation();
    e.preventDefault();

    if (
      e.button !== 0 ||
      args.filled
    ) {
      return;
    }

    let westBoundary: number = null;
    let eastBoundary: number = null;
    let northBoundary: number = null;
    let southBoundary: number = null;

    if (isWebsiteBuilderSectionEntityInSectionContext(args.context)) {
      const sectionRef = activePageDomNodeRefMap.current.get(args.context.parentNode.id);
      const sectionBoundingRect = sectionRef?.getBoundingClientRect();

      if (sectionBoundingRect == null) {
        return;
      }

      westBoundary = sectionBoundingRect.left + designSpaceBoundaries.leftX;
      eastBoundary = sectionBoundingRect.left + designSpaceBoundaries.rightX;
      northBoundary = sectionBoundingRect.top;
      southBoundary = sectionBoundingRect.bottom;
    } else if (args.context.type === "element-in-container") {
      const containerRef = activePageDomNodeRefMap.current.get(args.context.parentNode.id);
      const containerBoundingRect = containerRef?.getBoundingClientRect();

      if (containerBoundingRect == null) {
        return;
      }

      westBoundary = containerBoundingRect.left;
      eastBoundary = containerBoundingRect.right;
      northBoundary = containerBoundingRect.top;
      southBoundary = containerBoundingRect.bottom;
    } else if (args.context.type === "element-in-grid-cell") {
      const gridRef = activePageDomNodeRefMap.current.get(args.context.gridNode.id);
      const gridBoundingRect = gridRef?.getBoundingClientRect();

      if (gridBoundingRect == null) {
        return;
      }

      westBoundary = gridBoundingRect.left;
      eastBoundary = gridBoundingRect.right;
      northBoundary = gridBoundingRect.top;
      southBoundary = gridBoundingRect.bottom;
    } else if (args.context.type === "element-in-tab") {
      const tabRef = activePageDomNodeRefMap.current.get(args.context.parentNode.id);
      const tabBoundingRect = tabRef?.getBoundingClientRect();

      if (tabBoundingRect == null) {
        return;
      }

      westBoundary = tabBoundingRect.left;
      eastBoundary = tabBoundingRect.right;
      northBoundary = tabBoundingRect.top;
      southBoundary = tabBoundingRect.bottom;
    }

    if (
      westBoundary == null ||
      eastBoundary == null ||
      northBoundary == null ||
      southBoundary == null
    ) {
      return;
    }

    e.currentTarget.setPointerCapture(e.pointerId);

    resizeRefState.current.pointerId = e.pointerId;
    resizeRefState.current.currentTarget = e.currentTarget;

    const activeLayoutData = args.context.activeLayoutData;

    resizeRefState.current.prevDimensionRatios = {
      width: activeLayoutData.dimensions.width / activeLayoutData.dimensions.height,
      height: activeLayoutData.dimensions.height / activeLayoutData.dimensions.width,
    };

    resizeRefState.current.widthBasedAspectRatio =
      resizeRefState.current.prevDimensionRatios.width >= resizeRefState.current.prevDimensionRatios.height;
    
    resizeRefState.current.preserveAspectRatio =
      args.context.node.type === "element" &&
      isSectionEntityViewResizeAspectRatioElementType(args.context.node.element.type);

    resizeRefState.current.prevPosition = { ...activeLayoutData.position };
    resizeRefState.current.nextPosition = { ...activeLayoutData.position };

    resizeRefState.current.prevDimensions = { ...activeLayoutData.dimensions };
    resizeRefState.current.nextDimensions = { ...activeLayoutData.dimensions };

    resizeRefState.current.resizing = false;
    resizeRefState.current.resizingAllowed = true;

    setResizeState(prev => ({
      ...prev,
      handle,
      westBoundary,
      eastBoundary,
      northBoundary,
      southBoundary,
    }));
  };

  function getElementResizeHandleCategory(type: TWebsiteBuilderElementType): TWebsiteBuilderSectionEntityResizeHandleCategory {
    switch (type) {
      case "button":
      case "embed":
      case "icon":
      case "image":
      case "list":
      case "logo":
      case "navigation-menu":
      case "video":
        return allHandlesCategory;
      case "divider":
        return lateralHandlesCategory;
      case "heading":
      case "link":
      case "paragraph":
        return southOrLateralHandlesCategory;
      default:
        return null;
    }
  }

  function getContainerResizeHandleCategory(type: TWebsiteBuilderContainerType): TWebsiteBuilderSectionEntityResizeHandleCategory {
    switch (type) {
      case "box":
      case "flex":
      case "grid":
      case "marquee":
      case "slideshow":
      case "form":
      case "tabset":
        return allHandlesCategory;
      case "table":
        if (deviceView === "mobile") {
          return allHandlesCategory;
        } else {
          return cornerHandlesCategory;
        }
      default:
        return null;
    }
  }

  function renderResizeHandleCategory(category: TWebsiteBuilderSectionEntityResizeHandleCategory) {
    if (category == null || category.length === 0) {
      return null;
    }

    return (
      <>
        {category.map(handle => {
          if (!isWebsiteBuilderSectionEntityInSectionContext(args.context) || !args.context.stretch?.enabled || handle === "North" || handle === "South") {
            return (
              <SectionEntityViewResizeHandle
                key={handle}
                handle={handle}
                viewWidth={viewResizeDimensions.width}
                viewHeight={viewResizeDimensions.height}
                onPointerDown={onPointerDown}
              />
            );
          } else {
            return null;
          }
        })}
      </>
    );
  }

  function renderResizeHandles() {
    if (!args.selected || args.filled) {
      return null;
    }

    if (isWebsiteBuilderElementViewContext(args.context)) {
      return renderResizeHandleCategory(getElementResizeHandleCategory(args.context.node.element.type));
    } else if (isWebsiteBuilderContainerViewContext(args.context)) {
      return renderResizeHandleCategory(getContainerResizeHandleCategory(args.context.node.container.type));
    } else {
      return null;
    }
  }

  return {
    viewResizePosition,
    viewResizeDimensions,
    viewResizeHandles: renderResizeHandles(),
    viewResizeIndicator: <SectionEntityViewIndicator indicator={indicator} />,
  };
}
